import { useEffect, useState } from 'react';

import { EyeIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useToast } from 'hooks/toast';
import { useTranslation } from 'react-i18next';

import { EmailTemplateForm } from 'components/EmailTemplateForm/EmailTemplateForm';
import { EmailTemplatePreview } from 'components/EmailTemplatePreview/EmailTemplatePreview';
import { Button, FormMessage, InputGroup, MessageType, Select } from 'components/FormComponents';

import {
  useCreateEmailTemplateMutation,
  useLazyGetEmailTemplatesQuery,
} from 'api/email-templates-api';

const STANDARD_VALUE = '';

const getCompositeValue = template =>
  template.is_system_default ? `default-${template.id}` : `company-${template.id}`;

export const EmailTemplateStep = ({
  brand = {},
  category = 'candidate',
  type = null,
  submitLabel,
  footerLeft,
  standardName,
  standardDescription,
  previewStandardMessage,
  hideFooter = false,
  onSelectedTemplateIdChange,
  templateIdInBrandDefault,
  onBack,
  onSubmit = () => {},
}) => {
  const { t } = useTranslation();
  const showToast = useToast();

  const [selectedValue, setSelectedValue] = useState(STANDARD_VALUE);
  const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);
  const [templateIdInBrandDefaultValue, setTemplateIdInBrandDefaultValue] = useState(null);

  const [fetchTemplates, { data: templates = [], isFetching: isLoadingTemplates }] =
    useLazyGetEmailTemplatesQuery({ category, type });
  const [createEmailTemplate, { isLoading: isCreating }] = useCreateEmailTemplateMutation();

  useEffect(() => {
    fetchTemplates({ category, type });
  }, [category, type, fetchTemplates]);

  useEffect(() => {
    if (templateIdInBrandDefault == null || templateIdInBrandDefault === '') {
      return;
    }
    if (!templates.length) {
      return;
    }
    if (
      typeof templateIdInBrandDefault === 'string' &&
      /^(company|default)-\d+$/.test(templateIdInBrandDefault)
    ) {
      const match = templates.find(
        t => getCompositeValue(t) === templateIdInBrandDefault && !t.is_system_default,
      );
      if (match) {
        setTemplateIdInBrandDefaultValue(match.id);
        setSelectedValue(templateIdInBrandDefault);
      }
      return;
    }
    const want = Number(templateIdInBrandDefault);
    if (Number.isNaN(want)) {
      return;
    }
    const companyMatch = templates.find(t => !t.is_system_default && Number(t.id) === want);
    if (companyMatch) {
      setTemplateIdInBrandDefaultValue(want);
      setSelectedValue(getCompositeValue(companyMatch));
    }
  }, [templateIdInBrandDefault, templates]);

  const selectedTemplate = templates.find(tmpl => getCompositeValue(tmpl) === selectedValue);

  useEffect(() => {
    onSelectedTemplateIdChange?.(selectedTemplate ? getCompositeValue(selectedTemplate) : null);
  }, [selectedTemplate, onSelectedTemplateIdChange]);

  const handleCreateTemplate = async data => {
    const result = await createEmailTemplate(data);
    if (result.error) {
      showToast(t('common.error'), t('emailTemplates.errors.createFailed'), 'error');
      return;
    }
    const newTemplate = result.data;
    setSelectedValue(`company-${newTemplate.id}`);
    setIsCreatingTemplate(false);
    fetchTemplates({ category, type });
  };

  const handleSubmit = () => {
    onSubmit(selectedTemplate ? getCompositeValue(selectedTemplate) : null);
  };

  const standardTemplate = {
    id: null,
    name: standardName ?? t('addCheck.emailTemplate.standardName'),
    description: standardDescription ?? t('addCheck.emailTemplate.standardDescription'),
  };

  if (isCreatingTemplate) {
    return (
      <>
        <div className="flex items-center justify-between mb-4">
          <span className="text-2xl font-semibold text-neutral-7">
            {t('emailTemplates.form.createTitle')}
          </span>
          <Button type="link" onClick={() => setIsCreatingTemplate(false)}>
            {t('emailTemplates.backToSelect')}
          </Button>
        </div>
        <EmailTemplateForm
          onSubmit={handleCreateTemplate}
          onCancel={() => setIsCreatingTemplate(false)}
          isLoading={isCreating}
          initialValues={{ category }}
          showSupportFooterInPreview={brand?.include_support_footer ?? true}
        />
      </>
    );
  }

  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <div className="flex flex-col">
          <span className="text-2xl font-semibold text-neutral-7 mb-1">
            {t('addCheck.emailTemplate.title')}
          </span>
          <p className="text-xl text-neutral-6 mb-5">{t('addCheck.emailTemplate.subtitle')}</p>
        </div>
        <button
          type="button"
          className="text-xl font-semibold text-primary-4 bg-primary-1 hover:bg-primary-5 hover:text-neutral-1 px-3 py-1 rounded-full transition-colors"
          onClick={() => setIsCreatingTemplate(true)}>
          + {t('emailTemplates.createButton')}
        </button>
      </div>

      {isLoadingTemplates ? (
        <p className="text-xl text-neutral-5 py-4 text-center">{t('common.loading')}</p>
      ) : (
        <>
          <InputGroup title={t('addCheck.emailTemplate.selectLabel')}>
            <Select
              name="emailTemplateSelect"
              className="w-full"
              value={selectedValue}
              onChange={e => setSelectedValue(e.target.value)}>
              {!templateIdInBrandDefault && (
                <Select.Item value={STANDARD_VALUE} description={standardTemplate.description}>
                  {standardTemplate.name}
                </Select.Item>
              )}
              {templates
                .filter(template => !template.is_system_default)
                .map(template => {
                  const compositeValue = getCompositeValue(template);
                  const suffix =
                    templateIdInBrandDefaultValue === template.id ? ' (Brand Default)' : '';
                  return (
                    <Select.Item
                      key={compositeValue}
                      value={compositeValue}
                      description={template.subject}>
                      {template.name}
                      {suffix}
                    </Select.Item>
                  );
                })}
            </Select>
          </InputGroup>

          <div>
            {selectedValue === STANDARD_VALUE ? (
              <FormMessage
                type={MessageType.Info}
                message={previewStandardMessage ?? t('addCheck.emailTemplate.previewStandard')}
                noMargin
                closeable={false}
              />
            ) : selectedTemplate ? (
              <EmailTemplatePreview
                brand={brand}
                subject={selectedTemplate.subject}
                body={selectedTemplate.body}
                showSupportFooter={brand?.include_support_footer ?? true}
                showActions
              />
            ) : null}
          </div>
        </>
      )}

      {!hideFooter && (
        <div className="flex flex-wrap items-center justify-between gap-3 mt-6">
          <div className="flex flex-wrap items-center gap-3">{footerLeft}</div>
          <div className="flex flex-wrap items-center gap-3 ml-auto">
            {onBack && (
              <Button type="secondary" onClick={onBack}>
                {t('common.back')}
              </Button>
            )}
            <Button loading={isCreating} onClick={handleSubmit}>
              {submitLabel ?? t('addCheck.sendButton')}
            </Button>
          </div>
        </div>
      )}
    </>
  );
};
